import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Link, useLocation } from 'react-router-dom';
import ClassNames from 'classnames';
import { makeSelectAllowSignIn } from 'containers/Main/selectors';
import Mixpanel from 'utils/mixpanelService';
import _get from 'lodash/get';
import IconButton from '@material-ui/core/IconButton';
import HeartEmptyIcon from 'components/SaveAsFavorite/images/heart-empty-white.svg';
import HeartFullIcon from 'components/SaveAsFavorite/images/heart-full-white.svg';
import _isNil from 'lodash/isNil';
import { getImageFile, fallbackImage, isBot } from 'utils/stringUtils';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const useStyles = makeStyles(() => ({
  icon: {
    position: 'absolute',
    top: 7,
    right: 14,
    padding: 0,
    borderRadius: 0,
  },
  cardLink: {
    textDecoration: 'none',
    color: '#979797',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '0 10px',
  },
  title: {
    width: '100%',
    display: '-webkit-box',
    paddingTop: 7,
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 48 /* Fallback for non-webkit */,
    wordBreak: 'break-word',
  },
  description: {
    width: '100%',
    display: '-webkit-box',
    paddingTop: 7,
    fontSize: 12,
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxHeight: 50 /* Fallback for non-webkit */,
    wordBreak: 'break-word',
  },
  heart: {
    width: 20,
  },
  wrapContent: {
    position: 'relative',
  },
  img: {
    width: '100%',
    borderRadius: 20,
    height: 'auto',
    display: 'block',
  },
}));

function TopicCard(props) {
  const classes = useStyles();
  const {
    topicID,
    fields: { slug, title, description },
    fields,
    types,
    saved,
    saveTopic,
    srcPage,
  } = props;
  const location = useLocation();
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const redirect = () => {
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: title,
      type: 'topic',
      page: finalPageName,
      path: location.pathname.split('/')[1],
      slug: location.pathname.split('/')[2],
    });
  };
  const bot = isBot();
  const baseSlugUrl = `/topics/${slug}`;
  const redirectUrl = types ? `${baseSlugUrl}?types=${types}` : baseSlugUrl;
  const hasTopicImage =
    !_isNil(_get(fields, 'image.fields.file.url')) ||
    !_isNil(_get(fields, 'imageUrl')) ||
    !_isNil(_get(fields, 'image.url'));
  const imgUrl = hasTopicImage ? getImageFile(fields) : fallbackImage;

  return (
    <Grid
      item
      xs={12}
      key={slug}
      className={ClassNames(srcPage === 'favorites' && classes.wrapContent)}
    >
      <Link to={redirectUrl} onClick={redirect} className={classes.cardLink}>
        <Grid container>
          <Grid item xs={12}>
            <LazyLoadImage src={imgUrl} className={classes.img} alt="Topic" />
          </Grid>
          <Grid item xs={12}>
            <div className={ClassNames(classes.textContainer)}>
              {allowSignIn && saved && !bot && (
                <IconButton
                  className={classes.icon}
                  onClick={e => saveTopic(topicID, fields, true, e)}
                  disableRipple
                >
                  <img
                    src={HeartFullIcon}
                    alt="Full"
                    className={classes.heart}
                  />
                </IconButton>
              )}
              {allowSignIn && !saved && !_isNil(saved) && !bot && (
                <IconButton
                  className={classes.icon}
                  onClick={e => saveTopic(topicID, fields, false, e)}
                  disableRipple
                >
                  <img
                    src={HeartEmptyIcon}
                    alt="Empty"
                    className={classes.heart}
                  />
                </IconButton>
              )}
              <Typography
                variant="h6"
                align="left"
                color="textPrimary"
                className={classes.title}
              >
                {title}
              </Typography>
              <Typography
                align="left"
                color="textPrimary"
                className={classes.description}
              >
                {description}
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Link>
    </Grid>
  );
}

TopicCard.propTypes = {
  topicID: PropTypes.string,
  fields: PropTypes.object,
  saved: PropTypes.bool,
  saveTopic: PropTypes.func,
  types: PropTypes.string,
  srcPage: PropTypes.string,
};

export default TopicCard;
